import React, { useEffect, useRef, useState } from "react";
import { useElements } from "@stripe/react-stripe-js";
import { getStripeExpressCheckoutElementOptions } from "../../utils/stripePaymentUi";

/**
 * Google Pay / Apple Pay / Link buttons above the Payment Element.
 * Falls back gracefully if the browser or domain is not eligible.
 */
const StripeExpressCheckout = ({ onConfirm, className = "" }) => {
  const elements = useElements();
  const containerRef = useRef(null);
  const onConfirmRef = useRef(onConfirm);
  const [hasWalletButtons, setHasWalletButtons] = useState(false);
  const [failed, setFailed] = useState(false);

  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (!elements || !containerRef.current) return undefined;

    let expressEl = null;
    let cancelled = false;

    const handleConfirm = (event) => {
      if (typeof onConfirmRef.current === "function") {
        onConfirmRef.current(event);
      }
    };

    const updateAvailability = (availablePaymentMethods) => {
      if (!availablePaymentMethods) {
        setHasWalletButtons(false);
        return;
      }
      setHasWalletButtons(
        Boolean(availablePaymentMethods.googlePay || availablePaymentMethods.applePay)
      );
    };

    const handleReady = (event) => {
      updateAvailability(event?.availablePaymentMethods);
    };

    const handleAvailableChange = (event) => {
      updateAvailability(event?.availablePaymentMethods);
    };

    const handleLoadError = (event) => {
      setFailed(true);
      setHasWalletButtons(false);
      console.warn(
        "[Stripe] Express Checkout could not load:",
        event?.error?.message || "unknown error"
      );
    };

    try {
      expressEl = elements.create(
        "expressCheckout",
        getStripeExpressCheckoutElementOptions()
      );
      expressEl.on("confirm", handleConfirm);
      expressEl.on("ready", handleReady);
      expressEl.on("availablepaymentmethodschange", handleAvailableChange);
      expressEl.on("loaderror", handleLoadError);
      expressEl.mount(containerRef.current);
    } catch (err) {
      setFailed(true);
      console.warn("[Stripe] Express Checkout mount failed:", err?.message || err);
    }

    return () => {
      cancelled = true;
      if (!expressEl) return;
      try {
        expressEl.off("confirm", handleConfirm);
        expressEl.off("ready", handleReady);
        expressEl.off("availablepaymentmethodschange", handleAvailableChange);
        expressEl.off("loaderror", handleLoadError);
        expressEl.unmount();
      } catch {
        // ignore unmount errors during teardown
      }
      expressEl = null;
    };
  }, [elements]);

  if (failed) return null;

  return (
    <div
      className={className}
      style={{
        minHeight: hasWalletButtons ? undefined : 0,
        marginBottom: hasWalletButtons ? undefined : 0,
      }}
    >
      <div ref={containerRef} className="stripe-express-checkout-wrap w-full" />
    </div>
  );
};

export default StripeExpressCheckout;
